import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PdsLoadingPage } from "@principal/design-system-react";
import { generateToken, validateLink } from "../../service/GuestAuthService";
import { useGuestAuth } from "../guest/GuestAuthProvider";
import StatusMessage from "../common/StatusMessage";

type ValidationStatus = 'validating' | 'expired' | 'error';

const GuestValidate = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setGuestAuth } = useGuestAuth();
    const coverage = params?.coverage;
    const claimId = params?.claimId;
    const party = params?.party;
    const code = searchParams.get('code');
    const formState = searchParams.get('formState');
    const [magicLinkCode, setMagicLinkCode] = useState<string | null>(code);
    const [status, setStatus] = useState<ValidationStatus>('validating');
    const hasStarted = useRef(false);

    useEffect(() => {
        if (hasStarted.current) {
            return;
        }
        hasStarted.current = true;

        async function verifyLink() {
            if (!code) {
                if (formState) {
                    const recoverPath = claimId
                        ? `/${coverage}/${claimId}/${party}/guest/recover`
                        : `/${coverage}/${party}/guest/recover`;
                    navigate(`${recoverPath}?formState=${encodeURIComponent(formState)}`, { replace: true });
                    return;
                }

                setStatus('error');
                return;
            }

            let validationStatus: number;
            try {
                const linkContext = claimId && coverage && party
                    ? { benefitType: coverage, party, claimId }
                    : undefined;
                validationStatus = linkContext
                    ? await validateLink(code, linkContext)
                    : await validateLink(code);
            } catch {
                setStatus('error');
                return;
            }

            if (validationStatus === 401) {
                setStatus('expired');
                return;
            }
            if (validationStatus !== 200) {
                setStatus('error');
                return;
            }

            try {
                const tokenPayload = await generateToken(magicLinkCode ?? undefined);
                setGuestAuth(tokenPayload);
            } catch (error) {
                if ((error as { status?: number })?.status === 401) {
                    setStatus('expired');
                    return;
                }
                setStatus('error');
                return;
            } finally {
                setMagicLinkCode(null);
            }

            const targetPath = claimId
                ? `/${coverage}/${claimId}/${party}`
                : `/${coverage}/${party}`;
            const targetSearch = formState
                ? `?formState=${encodeURIComponent(formState)}`
                : '';

            navigate(`${targetPath}${targetSearch}`, { replace: true });
        }

        verifyLink();
    }, [code, coverage, claimId, party, formState, magicLinkCode, navigate, setGuestAuth]);

    const goToLogin = () => navigate(`/${coverage}/${party}/login`);

    if (status === 'expired') {
        return (
            <StatusMessage
                title="Link expired"
                message="This link has expired. Please request a new link to continue."
                buttonContent="Okay"
                onAction={goToLogin}
            />
        );
    }

    if (status === 'error') {
        return (
            <StatusMessage
                title="Something went wrong"
                message="We could not verify your link. Please request a new link to continue."
                buttonContent="Request a new link"
                onAction={goToLogin}
            />
        );
    }

    return <PdsLoadingPage />;
};

export default GuestValidate;
